import { useEffect, useMemo, useState } from 'react';
import { tw } from 'twind';
import { useForm } from 'react-hook-form';
import { InputText, Button, Icon } from '@global-ecom/nitro-uds/elements';
import { useRouter } from 'next/router';

import {
  GraphQlError,
  useAddCouponToCartMutation,
  useRemoveCouponFromCartMutation,
} from '__generated__/graphql';
import { isNumber } from 'utils/isNumber';
import { extractCode, extractError } from 'utils/extractError';
import {
  AnalyticsEvents,
  event,
  formErrorEvent,
  FormType,
} from 'utils/analytics';
import { getIsMobile } from 'utils/media';
import { useFeature } from 'hooks/useFeature';
import { useTranslate } from 'hooks/useTranslations';
import { useValidation } from 'hooks/useValidation';
import { Button as LegacyLink } from 'ui/elements/Button';
import { Price } from 'ui/components/Price';
import { ActiveCart } from 'types/cart';

import { CartSummaryAccordion } from './CartSummaryAccordion';

type PromoCodeProps = {
  cart: ActiveCart;
  setCouponIsFetching: (boolean) => void;
};

export const PromoCodeSection = ({
  cart,
  setCouponIsFetching,
}: PromoCodeProps) => {
  const t = useTranslate();
  const [validate] = useValidation();
  const isMobile = getIsMobile();
  const router = useRouter();

  const promoCodeExpandedOnMobile = useFeature(
    'CART_SUMMARY_PROMO_CODE_EXPANDED_ON_MOBILE'
  );

  const promoCodeExpandedOnDesktopTablet = useFeature(
    'PROMO_CODE_ACCORDION_EXPANDED_BY_DEFAULT'
  );

  const showDiscountAmountAndTextEnabled = useFeature(
    'SHOW_DISCOUNT_AMOUNT_AND_TEXT_ENABLED'
  );

  const selectedPromoCode: FormType = useMemo(() => {
    const pathname = router.pathname;
    return pathname.includes('/checkout')
      ? FormType.CHECKOUT
      : FormType.CART_PROMO;
  }, [router.pathname]);

  const [addCouponToCartResult, addCouponToCart] = useAddCouponToCartMutation();

  const [removeCouponFromCartResult, removeCouponCode] =
    useRemoveCouponFromCartMutation();

  useEffect(() => {
    if (addCouponToCartResult.fetching || removeCouponFromCartResult.fetching) {
      setCouponIsFetching(true);
    } else setCouponIsFetching(false);
  }, [
    addCouponToCartResult.fetching,
    setCouponIsFetching,
    removeCouponFromCartResult.fetching,
  ]);

  const expandedOnInitialLoad = isMobile
    ? promoCodeExpandedOnMobile
    : promoCodeExpandedOnDesktopTablet;

  const couponCodeForm = useForm<{ promoCode: string }>();

  const [removing, setRemoving] = useState<string | undefined>(undefined);
  const addError = useMemo(() => {
    const errorCode = extractCode(addCouponToCartResult);

    if (!errorCode) return;

    return (
      extractError(addCouponToCartResult) ?? t('addCouponCodeUserInputError') // Fallback in case we don't get a message from the server
    );
  }, [addCouponToCartResult, t]);

  const removeError = useMemo(() => {
    const errorCode = extractCode(removeCouponFromCartResult);

    if (!errorCode) return;

    switch (errorCode) {
      case GraphQlError.BadUserInput:
        return t('removeCouponCodeUserInputError');
      default:
        return t('removeCouponCodeGenericError');
    }
  }, [removeCouponFromCartResult, t]);

  return (
    <CartSummaryAccordion
      dataTestId="promo-code-container-accordion"
      id="promo-code-accordion"
      title={t('promoCodePlaceholder')}
      headerClassNameConfig={{
        baseClassName: 'text-base sm:text-xl bg-puma-black-700 p-4 uppercase',
        expanded: 'pb-4 pl-4',
        closed: 'pb-4 pl-4 mb-6',
      }}
      bodyClassNameConfig={{
        baseClassName: 'pt-4 mb-6',
      }}
      expanded={expandedOnInitialLoad}
      className="bg-puma-black-700 p-5"
    >
      <div id="promo-code-container">
        <form
          data-test-id="promo-code-form"
          onSubmit={couponCodeForm.handleSubmit(async data => {
            // Add Google Analytics event to capture promo code
            event(AnalyticsEvents.PROMO_CODE_SUBMITTED, {
              'promo code': data.promoCode.toLowerCase(),
            });
            const operationResult = await addCouponToCart({
              input: {
                code: data.promoCode,
                basketId: cart?.id as string,
              },
            });
            const errorCode = extractCode(operationResult);
            const errorMessage = extractError(operationResult);
            if (errorCode && errorMessage) {
              formErrorEvent(selectedPromoCode, [errorCode]);
              return;
            }

            couponCodeForm.reset();
          })}
          className="flex space-x-2 w-full mb-4"
          noValidate
        >
          <InputText
            id="promo-code"
            data-test-id="promo-code"
            name="promoCode"
            showLabel={false}
            label={t('promoCodeLabel')}
            placeholder={t('promoCodeLabel')}
            ref={couponCodeForm.register({
              validate: validate.safeInput,
              required: t('requiredField'),
            })}
            errorText={
              addError ||
              removeError ||
              couponCodeForm.errors?.promoCode?.message
            }
            crossOrigin={undefined}
          />
          <Button
            label={t('apply')}
            disabled={
              !couponCodeForm.watch('promoCode') ||
              addCouponToCartResult.fetching
            }
            loading={addCouponToCartResult.fetching}
            type="submit"
            size="lg"
            dataTestId="apply-coupon-code"
          />
        </form>
        {(cart?.couponItems || []).length > 0 && (
          <div
            data-test-id="promo-code-accepted-container"
            className="flex flex-col items-center border rounded font-bold divide-y text-sm"
          >
            {cart?.couponItems.map(coupon => (
              <div
                key={coupon.couponItemId}
                className="w-full"
                data-test-id={`coupon-${coupon.couponItemId}`}
              >
                <div className="flex items-center justify-between w-full p-3 rounded">
                  <div className="flex items-center space-x-3 justify-between ">
                    {coupon.statusCode === 'applied' ? (
                      <Icon
                        name="check"
                        size="xl"
                        fill="success"
                        dataTestId="promo-code-tick-icon"
                      />
                    ) : (
                      <Icon
                        name="exclamation-outline"
                        size="xl"
                        color="error"
                      />
                    )}
                    <p
                      data-test-id="promo-code-text"
                      className={tw(
                        'flex flex-wrap space-x-3',
                        coupon.statusCode !== 'applied' && 'text-puma-red'
                      )}
                    >
                      <span>{coupon.code}</span>
                      {showDiscountAmountAndTextEnabled &&
                        isNumber(coupon.discountAmount) && (
                          <span className="text-puma-green">
                            <Price amount={coupon.discountAmount} />
                          </span>
                        )}
                    </p>
                    {coupon.statusCode !== 'applied' && (
                      <span className="text-puma-red">
                        {t('notApplicable')}
                      </span>
                    )}
                  </div>
                  <LegacyLink
                    dataTestId="remove-coupon-code"
                    label={t('removeCoupon')}
                    variant="link"
                    disabled={!!removing}
                    loading={!!removing && removing === coupon.couponItemId}
                    onClick={async () => {
                      if (coupon.couponItemId) {
                        setRemoving(coupon.couponItemId);
                        await removeCouponCode({
                          input: {
                            basketId: cart?.id as string,
                            couponItemId: coupon.couponItemId,
                          },
                        });
                        setRemoving(undefined);
                      }
                    }}
                  >
                    {t('remove')}
                  </LegacyLink>
                </div>
                {coupon.calloutMessage && (
                  <p
                    className="p-3 pt-1 normal-case font-normal"
                    data-test-id="promo-callout-message"
                  >
                    {coupon.calloutMessage}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </CartSummaryAccordion>
  );
};
